import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { allProjects } from '@/mocks/projects';
import { useSEO, SITE_URL } from '@/hooks/useSEO';

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [activeImage, setActiveImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const project = allProjects.find((p) => p.id === id);
  const isTr = i18n.language === 'tr';

  useSEO(
    project
      ? {
          title: `${isTr ? project.title : project.titleEn} | Canel Elektrik Referans Proje`,
          description: `${isTr ? project.description : project.descriptionEn}`.substring(0, 155),
          keywords: `${isTr ? project.category : project.categoryEn}, Canel Elektrik proje, ${project.location}, endüstriyel elektrik taahhüt`,
          canonical: `/projects/${project.id}`,
          schema: {
            '@context': 'https://schema.org',
            '@type': 'CreativeWork',
            name: isTr ? project.title : project.titleEn,
            url: `${SITE_URL}/projects/${project.id}`,
            image: project.image,
            description: isTr ? project.description : project.descriptionEn,
            creator: {
              '@type': 'Organization',
              name: 'Canel Elektrik İnşaat Taahhüt Mühendislik',
              url: SITE_URL,
            },
            locationCreated: {
              '@type': 'Place',
              name: project.location,
            },
          },
        }
      : {
          title: 'Proje Detayı | Canel Elektrik',
          description: 'Canel Elektrik referans proje detay sayfası.',
          canonical: '/projects',
        }
  );

  useEffect(() => {
    setActiveImage(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!project) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center py-32">
          <i className="ri-error-warning-line text-6xl text-gray-300 mb-4" />
          <h2 className="text-2xl font-bold text-[#1A2332] mb-2">
            {i18n.language === 'tr' ? 'Proje Bulunamadı' : 'Project Not Found'}
          </h2>
          <p className="text-gray-500 mb-8">
            {i18n.language === 'tr'
              ? 'Aradığınız proje mevcut değil.'
              : 'The project you are looking for does not exist.'}
          </p>
          <Link
            to="/projects"
            className="bg-[#1A2332] text-white px-6 py-3 rounded-md font-semibold hover:bg-[#E8A020] transition-colors whitespace-nowrap"
          >
            {i18n.language === 'tr' ? 'Tüm Projelere Dön' : 'Back to All Projects'}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const images = [project.image, ...project.gallery.filter((g) => g !== project.image)];
  const displayTitle = isTr ? project.title : project.titleEn;
  const displayCategory = isTr ? project.category : project.categoryEn;
  const displayDescription = isTr ? project.description : project.descriptionEn;
  const displayScope = isTr ? project.scope : project.scopeEn;
  const displayTags = isTr ? project.tags : project.tagsEn;
  const otherProjects = allProjects.filter((p) => p.id !== project.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section - Full Width */}
      <section className="relative w-full h-[55vh] min-h-[420px] overflow-hidden">
        <img
          src={images[activeImage]}
          alt={displayTitle}
          className="w-full h-full object-cover object-top transition-all duration-700"
        />
        {/* Dark overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1520]/85 via-[#0d1520]/30 to-transparent" />

        {/* Bottom content on hero */}
        <div className="absolute bottom-0 left-0 right-0 pb-8 px-8">
          <div className="max-w-7xl mx-auto">
            <span className="inline-block bg-[#E8A020] text-white text-xs font-bold px-4 py-1.5 rounded-full mb-4 tracking-wide uppercase">
              {displayCategory}
            </span>
            <h1 className="text-3xl md:text-5xl font-black text-white leading-tight mb-4 max-w-3xl">
              {displayTitle}
            </h1>
            <div className="flex flex-wrap items-center gap-5 text-white/80 text-sm">
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-line text-[#E8A020]" />
                </div>
                {project.location}
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-building-2-line text-[#E8A020]" />
                </div>
                {project.client}
              </span>
              {project.area && (
                <span className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-layout-grid-line text-[#E8A020]" />
                  </div>
                  {project.area}
                </span>
              )}
            </div>
          </div>
        </div>


      </section>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="bg-[#0d1520] py-3 px-8">
          <div className="max-w-7xl mx-auto flex gap-2 overflow-x-auto">
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setActiveImage(idx)}
                className={`flex-shrink-0 w-20 h-14 rounded overflow-hidden border-2 transition-all cursor-pointer ${
                  activeImage === idx
                    ? 'border-[#E8A020] opacity-100'
                    : 'border-transparent opacity-50 hover:opacity-80'
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover object-top" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-12">

          {/* Left: Main content */}
          <div className="lg:col-span-2 space-y-12">

            {/* Description */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="w-1 h-7 bg-[#E8A020] rounded-full" />
                <h2 className="text-xl font-bold text-[#1A2332]">
                  {isTr ? 'Proje Hakkında' : 'About the Project'}
                </h2>
              </div>
              <p className="text-gray-600 text-base leading-relaxed">
                {displayDescription}
              </p>
            </div>

            {/* Scope of Work */}
            {displayScope.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-1 h-7 bg-[#E8A020] rounded-full" />
                  <h2 className="text-xl font-bold text-[#1A2332]">
                    {isTr ? 'Proje Kapsamı' : 'Scope of Work'}
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {displayScope.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 bg-[#f8f9fb] border border-gray-100 rounded-lg px-4 py-3.5 hover:border-[#E8A020]/30 transition-colors"
                    >
                      <div className="w-7 h-7 flex items-center justify-center bg-[#E8A020]/10 rounded-md flex-shrink-0">
                        <i className="ri-check-line text-[#E8A020] text-sm font-bold" />
                      </div>
                      <span className="text-sm text-[#1A2332] font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {displayTags.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-1 h-7 bg-[#E8A020] rounded-full" />
                  <h2 className="text-xl font-bold text-[#1A2332]">
                    {isTr ? 'Teknolojiler & Sistemler' : 'Technologies & Systems'}
                  </h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  {displayTags.map((tag) => (
                    <span
                      key={tag}
                      className="bg-[#1A2332] text-white text-sm px-4 py-2 rounded-full font-medium hover:bg-[#E8A020] transition-colors cursor-default"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Project Info Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-4">

              {/* Info Card */}
              <div className="bg-[#1A2332] rounded-xl overflow-hidden">
                <div className="px-6 py-4 border-b border-white/10">
                  <h3 className="text-sm font-bold text-white uppercase tracking-widest">
                    {isTr ? 'Proje Bİlgİlerİ' : 'Project Details'}
                  </h3>
                </div>
                <div className="p-6 space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#E8A020]/15 rounded-lg flex-shrink-0">
                      <i className="ri-building-2-line text-[#E8A020]" />
                    </div>
                    <div>
                      <p className="text-xs text-white/40 font-medium uppercase tracking-wider mb-1">
                        {isTr ? 'İşveren' : 'Client'}
                      </p>
                      <p className="text-sm font-semibold text-white leading-snug">{project.client}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#E8A020]/15 rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-[#E8A020]" />
                    </div>
                    <div>
                      <p className="text-xs text-white/40 font-medium uppercase tracking-wider mb-1">
                        {isTr ? 'Lokasyon' : 'Location'}
                      </p>
                      <p className="text-sm font-semibold text-white">{project.location}</p>
                    </div>
                  </div>

                  {project.area && (
                    <div className="flex items-start gap-4">
                      <div className="w-9 h-9 flex items-center justify-center bg-[#E8A020]/15 rounded-lg flex-shrink-0">
                        <i className="ri-layout-grid-line text-[#E8A020]" />
                      </div>
                      <div>
                        <p className="text-xs text-white/40 font-medium uppercase tracking-wider mb-1">
                          {isTr ? 'Alan' : 'Area'}
                        </p>
                        <p className="text-sm font-semibold text-white">{project.area}</p>
                      </div>
                    </div>
                  )}

                  {project.year && (
                    <div className="flex items-start gap-4">
                      <div className="w-9 h-9 flex items-center justify-center bg-[#E8A020]/15 rounded-lg flex-shrink-0">
                        <i className="ri-calendar-line text-[#E8A020]" />
                      </div>
                      <div>
                        <p className="text-xs text-white/40 font-medium uppercase tracking-wider mb-1">
                          {isTr ? 'Tamamlanma Yılı' : 'Completion Year'}
                        </p>
                        <p className="text-sm font-semibold text-white">{project.year}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-4">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#E8A020]/15 rounded-lg flex-shrink-0">
                      <i className="ri-price-tag-3-line text-[#E8A020]" />
                    </div>
                    <div>
                      <p className="text-xs text-white/40 font-medium uppercase tracking-wider mb-1">
                        {isTr ? 'Proje Türü' : 'Project Type'}
                      </p>
                      <p className="text-sm font-semibold text-white">{displayCategory}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA Card */}
              <div className="bg-[#E8A020]/10 border border-[#E8A020]/20 rounded-xl p-6">
                <div className="w-10 h-10 flex items-center justify-center bg-[#E8A020] rounded-lg mb-4">
                  <i className="ri-phone-line text-white text-lg" />
                </div>
                <h4 className="text-[#1A2332] font-bold text-base mb-2">
                  {isTr ? 'Benzer Proje mi İstiyorsunuz?' : 'Need a Similar Project?'}
                </h4>
                <p className="text-gray-500 text-sm mb-4 leading-relaxed">
                  {isTr
                    ? 'Uzman ekibimizle iletişime geçin, projenizi birlikte planlayalım.'
                    : 'Contact our expert team and let\'s plan your project together.'}
                </p>
                <Link
                  to="/contact"
                  className="w-full bg-[#E8A020] text-white text-sm font-bold py-3 rounded-lg flex items-center justify-center gap-2 hover:bg-[#d4911c] transition-colors cursor-pointer whitespace-nowrap"
                >
                  {isTr ? 'İletişime Geç' : 'Contact Us'}
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line" />
                  </div>
                </Link>
              </div>

              {/* Back button */}
              <button
                onClick={() => navigate(-1)}
                className="w-full bg-white border border-gray-200 text-[#1A2332] text-sm font-semibold py-3 rounded-lg flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line" />
                </div>
                {isTr ? 'Tüm Projelere Dön' : 'Back to All Projects'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Other Projects */}
      <section className="py-16 bg-[#f8f9fb]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center gap-3">
              <span className="w-1 h-7 bg-[#E8A020] rounded-full" />
              <h2 className="text-2xl font-black text-[#1A2332]">
                {isTr ? 'Diğer Projeler' : 'Other Projects'}
              </h2>
            </div>
            <Link
              to="/projects"
              className="text-sm font-semibold text-[#E8A020] hover:text-[#d4911c] flex items-center gap-1 transition-colors cursor-pointer whitespace-nowrap"
            >
              {isTr ? 'Tümünü Gör' : 'View All'}
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {otherProjects.map((p) => (
              <Link
                key={p.id}
                to={`/projects/${p.id}`}
                className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-[#E8A020]/40 transition-all duration-300 cursor-pointer"
              >
                <div className="relative w-full h-52 overflow-hidden">
                  <img
                    src={p.image}
                    alt={isTr ? p.title : p.titleEn}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-600"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A2332]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute top-3 left-3">
                    <span className="bg-[#E8A020] text-white text-xs font-bold px-3 py-1 rounded-full">
                      {isTr ? p.category : p.categoryEn}
                    </span>
                  </div>
                  <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-8 h-8 flex items-center justify-center bg-white rounded-full">
                      <i className="ri-arrow-right-up-line text-[#1A2332] text-sm" />
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-[#1A2332] text-sm leading-snug mb-3 group-hover:text-[#E8A020] transition-colors">
                    {isTr ? p.title : p.titleEn}
                  </h3>
                  <div className="flex items-center gap-4 text-xs text-gray-400 flex-wrap">
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-map-pin-line" />
                      </div>
                      {p.location}
                    </span>
                    {p.area && (
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-layout-grid-line" />
                        </div>
                        {p.area}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
          <img
            src={images[activeImage]}
            alt={displayTitle}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          {images.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); setActiveImage(idx); }}
                  className={`w-14 h-9 rounded overflow-hidden border-2 transition-all cursor-pointer ${
                    activeImage === idx ? 'border-[#E8A020]' : 'border-white/20 opacity-50 hover:opacity-80'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
